import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { promotionAvatar, playerAvatar, wrestlerAvatar } from "../lib/api";
import { Trophy, Shield, Users, Users2 } from "lucide-react";
import { ChampionCrown } from "../components/ChampionCrown";
import { GradCard, SectionHeading } from "../components/wrestler/WrestlerUI";
import { RankTable, PrestigeBackdrop } from "../components/leaderboard/LeaderboardUI";

const BG = "/backgroundgraphics/Wrestlerpage_Background.webp";

const BOARDS = {
  manager: {
    title: "Managers", icon: Users, color: "#FF007F", prefix: "lb", endpoint: "/leaderboard/v2", empty: "No champions yet. Be the first!",
    cols: [
      { id: "name", label: "Account", span: 3, align: "left", render: (r) => r.account_name },
      { id: "hof", sortable: true, label: "HOF", span: 2, color: "#B266FF", render: (r) => r.hof_points.toLocaleString() },
      { id: "wins", sortable: true, label: "Wins", span: 2, color: "#39FF14", render: (r) => r.pvp_wins },
      { id: "losses", sortable: true, label: "Losses", span: 1, color: "#FF007F", render: (r) => r.pvp_losses },
      { id: "streak", sortable: true, label: "Streak", span: 1, color: "#FFE600", render: (r) => r.win_streak },
      { id: "level", sortable: true, label: "Total LVL", span: 2, color: "#00F0FF", render: (r) => r.combined_level.toLocaleString() },
    ],
    key: (r, i) => `${r.account_name}-${i}`,
    path: (r) => r.account_name && `/profile/${encodeURIComponent(r.account_name)}`,
    podium: (r) => ({ title: r.account_name, avatars: [playerAvatar(r.account_name, r.player_avatar_updated_at)] }),
  },
  tagteam: {
    title: "Tag Teams", icon: Users2, color: "#39FF14", prefix: "tt-lb", endpoint: "/leaderboard/tag-teams", empty: "No tag teams formed yet.",
    cols: [
      { id: "name", label: "Tag Team", span: 5, align: "left", render: (r) => r.name },
      { id: "wins", sortable: true, label: "Wins", span: 2, color: "#39FF14", render: (r) => r.wins },
      { id: "losses", sortable: true, label: "Losses", span: 1, color: "#FF007F", render: (r) => r.losses },
      { id: "titles", sortable: true, label: "Titles", span: 1, color: "#FFD700", render: (r) => r.titles_won },
      { id: "hof", sortable: true, label: "HOF", span: 2, color: "#B266FF", render: (r) => r.hof_points },
    ],
    key: (r) => r.team_id,
    path: (r) => r.team_id && `/tag-team/${encodeURIComponent(r.team_id)}`,
    podium: (r) => ({ title: r.name, subtitle: (r.members || []).map((m) => m.wrestler_name).join(" & "),
                      avatars: (r.members || []).length ? r.members.map(wrestlerAvatar) : ["/wrestler_default.webp"] }),
  },
  promotion: {
    title: "Promotions", icon: Shield, color: "#00F0FF", prefix: "plb", endpoint: "/leaderboard/promotions", empty: "No promotions have been founded yet.",
    cols: [
      { id: "name", label: "Promotion", span: 3, align: "left", render: (r) => (
        <span className="flex items-center gap-2 min-w-0">
          <span className="relative shrink-0">
            <img src={promotionAvatar(r.promotion_id, r.avatar_updated_at)} alt="" className="w-8 h-8 rounded-lg object-cover border" style={{ borderColor: r.is_champion ? "#FFD700" : "#00F0FF80" }} />
            {r.is_champion && <ChampionCrown className="-top-2.5 left-1/2 -translate-x-1/2 w-4 h-4" />}
          </span>
          <span className="truncate">{r.name}</span>
        </span>
      ) },
      { id: "plevel", sortable: true, label: "LV", span: 1, color: "#39FF14", render: (r) => r.promo_level },
      { id: "hof", sortable: true, label: "HOF", span: 2, color: "#B266FF", render: (r) => r.hof_points.toLocaleString() },
      { id: "titles", sortable: true, label: "PW Titles", span: 2, color: "#FFD700", render: (r) => r.pw_wins },
      { id: "level", sortable: true, label: "Total LVL", span: 2, color: "#00F0FF", render: (r) => r.combined_level.toLocaleString() },
      { id: "mbrs", label: "Mbrs", span: 1, color: "#FFE600", render: (r) => `${r.member_count}/${r.member_cap}` },
    ],
    key: (r) => r.promotion_id,
    path: (r) => r.promotion_id && `/promotion/${encodeURIComponent(r.promotion_id)}`,
    podium: (r) => ({ title: r.name, subtitle: r.leader_name && `Led by ${r.leader_name}`, avatars: [promotionAvatar(r.promotion_id, r.avatar_updated_at)] }),
  },
};

function Board({ id }) {
  const b = BOARDS[id];
  const [sort, setSort] = useState("hof");
  const [rows, setRows] = useState(null);
  const [me, setMe] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    api.get(`${b.endpoint}?sort=${sort}`).then((r) => { setRows(r.data.items); setMe(r.data.me); }).catch(() => setRows([]));
  }, [b.endpoint, sort]);
  const open = (r) => { const p = b.path(r); if (p) nav(p); };
  return (
    <GradCard delay={120} bg={BG} testId={`${b.prefix}-board`}>
      <SectionHeading icon={b.icon} color={b.color}>{b.title}</SectionHeading>
      {rows && <RankTable key={sort} cols={b.cols} rows={rows} color={b.color} testPrefix={b.prefix} onOpen={open} rowKey={b.key} empty={b.empty} sort={sort} onSort={setSort} top={b.podium} me={me} />}
    </GradCard>
  );
}

const TABS = [
  { id: "manager", label: "Manager", icon: Users, color: "#FF007F", testId: "lb-tab-manager" },
  { id: "tagteam", label: "Tag Teams", icon: Users2, color: "#39FF14", testId: "lb-tab-tag-team" },
  { id: "promotion", label: "Promotions", icon: Shield, color: "#00F0FF", testId: "lb-tab-promotion" },
];

export default function LeaderboardPage() {
  const [tab, setTab] = useState("manager");
  return (
    <div className="relative max-w-5xl mx-auto px-4 py-8" data-testid="leaderboard-page">
      <PrestigeBackdrop />
      <div className="wr-drift pointer-events-none absolute top-0 left-10 w-72 h-72 rounded-full blur-3xl opacity-20 bg-[#FF007F]" />
      <div className="wr-drift pointer-events-none absolute top-40 right-0 w-72 h-72 rounded-full blur-3xl opacity-15 bg-[#00F0FF]" style={{ animationDelay: "-5s" }} />
      <div className="relative wr-rise flex items-center gap-3 mb-1">
        <Trophy size={40} strokeWidth={2.6} className="wr-crown text-[#FFD700]" style={{ filter: "drop-shadow(0 0 10px #FFD700)" }} />
        <h1 className="font-graffiti text-4xl sm:text-5xl leading-none" data-testid="lb-title">
          <span style={{ color: "#FF007F", textShadow: "0 0 18px #FF007F99, 3px 3px 0 #000" }}>GLOBAL</span>{" "}
          <span style={{ color: "#00F0FF", textShadow: "0 0 18px #00F0FF99, 3px 3px 0 #000" }}>HIGHSCORE</span>
        </h1>
      </div>
      <p className="relative text-[#A89BCC] font-mono text-xs mb-5">Click column headers to sort · click a row to open profile</p>
      <div className="relative flex flex-wrap gap-2 mb-5" data-testid="lb-tabs">
        {TABS.map((t) => {
          const on = tab === t.id;
          return (
            <button key={t.id} data-testid={t.testId} onClick={() => setTab(t.id)}
                    className="px-4 py-2 rounded-xl font-heading text-xs uppercase tracking-widest flex items-center gap-2 border-2 transition-[background-color,box-shadow,transform] duration-200 hover:-translate-y-0.5"
                    style={{ borderColor: on ? t.color : `${t.color}44`, background: on ? `linear-gradient(180deg, ${t.color}40, ${t.color}10)` : "#0A051A", color: on ? "#fff" : "#A89BCC", boxShadow: on ? `0 0 16px ${t.color}66` : "none" }}>
              <t.icon size={15} style={{ color: t.color, filter: on ? `drop-shadow(0 0 4px ${t.color})` : "none" }} /> {t.label}
            </button>
          );
        })}
      </div>
      <div className="relative"><Board key={tab} id={tab} /></div>
    </div>
  );
}
